import { EmployeeDataType } from './../../../data-management/data-sources/sources/employees-data-source';

import { ResourceLoader } from './../resource-loader';
import { Gui } from './../../gui';

// import html
import template from './organigram-component.hogan.html';

// js
import { TeamNode } from './visual/team-node';
import { EmployeeNode } from './visual/employee-node';
import { NonTechyConsultantNode } from './visual/non-techy-consultant-node';
import { TechyConsultantNode } from './visual/techy-consultant-node';
import { OperationsNode } from './visual/operations-node';
import { VisualNode } from './visual/visual-node';
import * as Snap from 'snapsvg-cjs';
import store from '@/store/store';

export class OrganigramComponent extends ResourceLoader {
    public snap: any;
    public width: number;
    public height: number;
    public organigramSize: number;
    private operationsNode: OperationsNode;
    private employeeIdToNodes: { [id: string]: EmployeeNode[] } = {};
    public tooltipLayer: any;

    constructor(private gui: Gui) {
        super(template, {}, [
            { name: 'techSymbol', path: 'img/tech.svg' },
            { name: 'nontechSymbol', path: 'img/nontech.svg' }
        ]);

        this.snap = new Snap(this.jqueryElement.find('.organigram-svg')[0]);
    }

    public init() {
        return new Promise(resolve => {
            if (this.areResourcesLoaded()) {
                this.initOrganigram();
                this.populateOperations();
                this.drawAccordingToZindexes();
                this.subscribeToReset();
                resolve(true);
            } else {
                setTimeout(() => this.init().then(() => resolve(true)), 100);
            }
        });
    }

    public subscribeToReset = () => {};

    public appendSymbol(svgSymbolName: string) {
        const svgSymbol = this.retrieveSymbol(svgSymbolName);
        this.snap.append(svgSymbol);
        return svgSymbol;
    }

    private initOrganigram() {
        const organigram = this.jqueryElement.find('.organigram-svg');
        this.width = organigram.innerWidth();
        this.height = organigram.innerHeight();
        this.organigramSize = Math.min(this.width, this.height);

        // Create tooltip layer LAST so it draws on top
        this.tooltipLayer = this.snap.g().attr({ id: 'tooltip-layer' });

        this.operationsNode = new OperationsNode(this);
        this.initListenToFilter();
    }

    private initListenToFilter() {
        const filtersMerger = store.state.dataFiltersManager.allFiltersMerger;
        filtersMerger.filterUpdatedEvent.attach(() => {
            filtersMerger.hiddenData.forEach((el: EmployeeDataType) => {
                const nodes = this.employeeIdToNodes[el.Id];
                if (nodes && nodes.length) {
                    nodes.forEach(node => node.disable());
                }
            });
            filtersMerger.filteredData.forEach((el: EmployeeDataType) => {
                const nodes = this.employeeIdToNodes[el.Id];
                if (nodes && nodes.length) {
                    if (filtersMerger.anyFilterApplied) {
                        nodes.forEach(node => node.enable());
                    } else {
                        nodes.forEach(node => node.disable());
                    }
                }
            });
        });
    }

    private drawAccordingToZindexes() {
        VisualNode.layerManager.drawInOrder();
    }

    private populateOperations() {
        const teamNodes: any[] = [];
        const edges: any[] = [];
        const contractsToEmployees = this.gui.app.dataSourceLoader.employeesToAllocationsDataSource
            .allocationsToEmployees;
        const allocations = this.gui.app.dataSourceLoader.employeesToAllocationsDataSource.allocationsHash;

        Object.keys(contractsToEmployees).forEach(contractName => {
            if (contractName !== 'AE nv') {
                const allocation = allocations[contractName];
                const team = {
                    id: allocation.AccountId,
                    accountName: contractName,
                    accountClusterId: allocation.AccountClusterId,
                    employees: contractsToEmployees[contractName]
                };
                const teamNode = new TeamNode(team, this, this.gui);
                teamNodes.push(teamNode);
                this.operationsNode.addChild(teamNode);
                team.employees.forEach(employee => {
                    // people who work internally (technontech == null) should not be on bench
                    // but CAN be on an account.
                    if (team.accountName !== 'Availability' || employee.TechNonTech !== null) {
                        const node = this.createEmployeeNode(employee);
                        teamNode.addChild(node);
                        if (!this.employeeIdToNodes[employee.Id]) {
                            this.employeeIdToNodes[employee.Id] = [];
                        }
                        this.employeeIdToNodes[employee.Id].push(node);
                        edges.push({ from: team, to: node });
                    }
                });
            }
        });
        this.operationsNode.refresh();
    }

    public ensureTooltipLayerOnTop() {
        if (this.tooltipLayer) {
            this.tooltipLayer.appendTo(this.snap); // re-append => last => top
        }
    }

    public resize() {
        const w = this.jqueryElement.find('.organigram-svg').innerWidth();
        const h = this.jqueryElement.find('.organigram-svg').innerHeight();

        if (w !== this.width || h !== this.height) {
            this.width = w;
            this.height = h;
            this.organigramSize = Math.min(this.width, this.height);
            this.operationsNode.refresh();

            this.ensureTooltipLayerOnTop();
        }
    }

    private createEmployeeNode(employee: EmployeeDataType, nodeGroup: any = null) {
        let node: EmployeeNode;
        if (employee.TechNonTech === 'Technical') {
            node = new TechyConsultantNode(employee, this, this.gui, nodeGroup);
        } else {
            node = new NonTechyConsultantNode(employee, this, this.gui, nodeGroup);
        }
        return node;
    }
}
